import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

import { AuthProvider } from './AuthContext';
import { PublicClientApplication, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { createMsalConfig } from './authConfig';
import { MsalProvider } from '@azure/msal-react';

import '@fontsource/roboto';
import '@fontsource/roboto-mono';
import 'material-icons/iconfont/material-icons.css';

import { datadogRum } from '@datadog/browser-rum';
import packageJson from '../package.json';

const applyDatadogUser = (account) => {
  if (!account) {
    datadogRum.setUser({});
    return;
  }

  const { homeAccountId, localAccountId, username, name } = account;
  const id = homeAccountId || localAccountId || username || 'unknown-account';

  datadogRum.setUser({
    id,
    email: username || undefined,
    name: name || undefined
  });
};

/* ----------  Root component  ---------- */
const Root = () => {
  const [msalInstance, setMsalInstance] = useState(null);
  const [baseConfig, setBaseConfig] = useState(null);
  const datadogInitRef = useRef(false);

  const needsInteractiveLogin = useCallback((error) => {
    if (!error) {
      return false;
    }
    const code = error.errorCode || error.subError || '';
    if (typeof InteractionRequiredAuthError !== 'undefined' && typeof InteractionRequiredAuthError.isInteractionRequiredError === 'function' && InteractionRequiredAuthError.isInteractionRequiredError(code)) {
      return true;
    }

    return ['monitor_window_timeout', 'token_renewal_error', 'no_tokens_found', 'interaction_required'].includes(code);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        /* 1️⃣ Load backend config (skip auth) */
        const cfg = await fetch('/api/config', { headers: { 'x-no-auth': 'true' } }).then(r => r.json());
        setBaseConfig(cfg);
        /* 2️⃣ Create + initialize MSAL */
        const instance = new PublicClientApplication(createMsalConfig(cfg));
        await instance.initialize();                 // ⭐ REQUIRED
        await instance.handleRedirectPromise();      // ⭐ Handle any pending redirects

        /* 3️⃣ Pick first cached account if none active */
        if (!instance.getActiveAccount() && instance.getAllAccounts().length) {
          instance.setActiveAccount(instance.getAllAccounts()[0]);
        }

        applyDatadogUser(instance.getActiveAccount());

        setMsalInstance(instance);
      } catch (err) {
        console.error('MSAL bootstrap failed:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!msalInstance || !baseConfig) return;

    const scope = baseConfig.SP_API_ENDPOINT;

    const fetchAuthConfig = async (account) => {
      if (!account || !scope) return;
      if (datadogInitRef.current) return;
      try {
        let tokenResponse;
        try {
          tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [scope],
            account,
          });
        } catch (err) {
          if (!needsInteractiveLogin(err)) {
            throw err;
          }
          try {
            tokenResponse = await msalInstance.acquireTokenPopup({
              scopes: [scope],
              account,
            });
          } catch (popupError) {
            if (!needsInteractiveLogin(popupError)) {
              throw popupError;
            }
            await msalInstance.acquireTokenRedirect({
              scopes: [scope],
              account,
            });
            return;
          }
        }
        const authCfg = await fetch('/api/auth-config', {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        }).then((r) => r.json());

        if (!datadogInitRef.current) {
          datadogRum.init({
            applicationId: authCfg.DD_APPLICATION_ID,
            clientToken: authCfg.DD_CLIENT_TOKEN,
            site: 'datadoghq.eu',
            service: 'trustnest-portal-v2',
            env: authCfg.DD_ENV || 'production',
            version: packageJson.version,
            sampleRate: 100,
            trackInteractions: true,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 20,
            defaultPrivacyLevel: 'mask-user-input',
          });
          datadogRum.startSessionReplayRecording();
          datadogInitRef.current = true;
        }
      } catch (err) {
        console.warn('Unable to fetch authenticated config:', err);
      }
    };

    const ensureAuthConfig = async () => {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (account) {
        await fetchAuthConfig(account);
        applyDatadogUser(account);
      }
    };

    ensureAuthConfig();

    const callbackId = msalInstance.addEventCallback(async (ev) => {
      if (ev.eventType === EventType.LOGIN_SUCCESS && ev.payload?.account) {
        msalInstance.setActiveAccount(ev.payload.account);
        await fetchAuthConfig(ev.payload.account);
        applyDatadogUser(ev.payload.account);
      }

      if (ev.eventType === EventType.LOGOUT_SUCCESS) {
        applyDatadogUser(null);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [msalInstance, baseConfig, needsInteractiveLogin]);

  if (!msalInstance) {
    return <div>Loading authentication…</div>;
  }

  return (
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </MsalProvider>
    </React.StrictMode>
  );
};

/* ----------  Render  ---------- */
ReactDOM.createRoot(document.getElementById('root')).render(<Root />);
